import { useCallback, useEffect, useRef, useState } from "react";

export interface ElementSize {
  width: number;
  height: number;
}

export interface UseElementSizeResult<T extends HTMLElement> {
  /** Current size of the observed element. 0 until first measurement. */
  size: ElementSize;
  /** Attach to the element you want to observe. */
  ref: React.RefObject<T | null>;
  /** Imperatively re-measure — useful after drag-resize ancestors or delayed mounts. */
  measure: () => void;
}

/**
 * Observe an element's size using ResizeObserver, with rAF debouncing.
 *
 * Mirrors the pattern from react-grid-layout's `useContainerWidth`, but
 * independent of that library so any component can use it. Returns `width`
 * and `height`, plus an imperative `measure()` in case the observed element
 * was mounted after the hook's initial effect fired (e.g. gated on a loading
 * state).
 *
 * @example
 * ```tsx
 * const { size, ref } = useElementSize<HTMLDivElement>();
 * return <div ref={ref}>Width: {size.width}</div>;
 * ```
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>(): UseElementSizeResult<T> {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    setSize({ width: node.offsetWidth, height: node.offsetHeight });
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    let rafId: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
        rafId = null;
      });
    });
    observer.observe(node);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [measure]);

  return { size, ref, measure };
}
